import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

/**
 * Growth Diagnostics — the "where are we lagging in Agra?" report. Four live metrics:
 *  1. Activation funnel (signup→order + in-app app_open→…→order drop-off)
 *  2. Repeat cohort (1 / 2–3 / 4+ orders, + GMV concentration)
 *  3. Price truth vs Zomato/Swiggy (from the daily win/loss snapshot)
 *  4. Selection — demand (orders) vs supply (live restaurants) by cuisine
 * All numbers come straight from the backend aggregation — nothing hardcoded.
 */

const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', marginBottom: 16 };
const kpi = { ...card, textAlign: 'center', marginBottom: 0 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#777', borderBottom: '2px solid #eee', textTransform: 'uppercase' };
const td = { padding: '8px 10px', fontSize: 14, borderBottom: '1px solid #f3f3f3' };

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }));
const money = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
const pc = (n) => (n == null ? '—' : `${n}%`);

function GrowthDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await adminAPI.getGrowthDiagnostics();
      setData(r.data.data);
    } catch (e) { setErr(e.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<div><Navbar /><div style={{ padding: 24 }}>🔄 Crunching live data…</div></div>);
  if (err) return (<div><Navbar /><div style={{ padding: 24, color: '#EF4444' }}>{err} <button onClick={load}>Retry</button></div></div>);
  if (!data) return (<div><Navbar /><div style={{ padding: 24 }}>No data</div></div>);

  const { overview, activation, funnel, repeat, selection, price } = data;

  // Biggest drop-off step in the behavioral funnel (lowest step-to-step conversion).
  const fsteps = (funnel && funnel.steps) || [];
  let worstStep = null;
  fsteps.forEach((s) => { if (s.convFromPrevPct != null && (worstStep == null || s.convFromPrevPct < worstStep.convFromPrevPct)) worstStep = s; });
  const maxFunnel = fsteps.length ? Math.max(...fsteps.map((s) => s.users || 0), 1) : 1;

  const rb = repeat?.buckets || {};

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h1 style={{ marginBottom: 2 }}>📊 Growth Diagnostics</h1>
          <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>↻ Refresh</button>
        </div>
        <p style={{ color: '#777', marginTop: 0 }}>
          Where are we actually lagging — acquisition, activation, retention, price, or selection? Live from orders/users/events.
          {data.generatedAt && <span style={{ fontSize: 12 }}> · generated {new Date(data.generatedAt).toLocaleString('en-IN')}</span>}
        </p>

        {/* Overview KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Signups (customers)</div><div style={{ fontSize: 24, fontWeight: 700 }}>{nf(overview.signups)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Ever ordered</div><div style={{ fontSize: 24, fontWeight: 700 }}>{nf(overview.buyers)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Activation</div><div style={{ fontSize: 24, fontWeight: 700, color: (overview.activationPct || 0) < 20 ? '#EF4444' : '#059669' }}>{pc(overview.activationPct)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Delivered orders</div><div style={{ fontSize: 24, fontWeight: 700 }}>{nf(overview.deliveredOrders)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>GMV (delivered)</div><div style={{ fontSize: 24, fontWeight: 700 }}>{money(overview.totalGmv)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Live restaurants</div><div style={{ fontSize: 24, fontWeight: 700 }}>{nf(overview.liveRestaurants)}</div></div>
        </div>

        {/* 1. ACTIVATION */}
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>1 · Activation — do signups ever order?</h3>
          <p style={{ color: '#555', marginTop: 0 }}>
            <b style={{ color: (activation.activationPct || 0) < 20 ? '#EF4444' : '#059669' }}>{pc(activation.activationPct)}</b> of {nf(activation.signups)} signups have ever placed an order.
            {' '}<b>{pc(activation.neverOrderedPct)}</b> never ordered once. Of those who did, <b>{pc(activation.secondOrderRatePct)}</b> came back for a 2nd order (avg {activation.avgOrdersPerBuyer} orders/buyer).
          </p>

          <h4 style={{ marginBottom: 6 }}>In-app funnel · last {funnel?.windowDays || 30} days (distinct logged-in users)</h4>
          {worstStep && <p style={{ fontSize: 13, color: '#B45309', marginTop: 0 }}>⚠ Biggest drop-off: <b>{worstStep.label}</b> — only {pc(worstStep.convFromPrevPct)} of the previous step continue.</p>}
          {fsteps.map((s) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 150, fontSize: 13, color: '#444' }}>{s.label}</div>
              <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(2, (s.users / maxFunnel) * 100)}%`, background: worstStep && s.key === worstStep.key ? '#F59E0B' : '#6366F1', color: '#fff', fontSize: 12, padding: '4px 8px', whiteSpace: 'nowrap' }}>{nf(s.users)}</div>
              </div>
              <div style={{ width: 70, fontSize: 12, color: '#777', textAlign: 'right' }}>{s.convFromPrevPct != null ? `${s.convFromPrevPct}%` : '—'}</div>
            </div>
          ))}

          <h4 style={{ marginBottom: 6, marginTop: 16 }}>Monthly signup cohorts — % that ever ordered</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Month</th><th style={th}>Signups</th><th style={th}>Activated</th><th style={th}>Activation %</th></tr></thead>
            <tbody>
              {(activation.cohorts || []).map((c) => (
                <tr key={c.month}><td style={td}>{c.month}</td><td style={td}>{nf(c.signups)}</td><td style={td}>{nf(c.activated)}</td>
                  <td style={{ ...td, fontWeight: 600, color: (c.activationPct || 0) < 20 ? '#EF4444' : '#059669' }}>{pc(c.activationPct)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 2. REPEAT */}
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>2 · Repeat cohort — do buyers stick?</h3>
          <p style={{ color: '#555', marginTop: 0 }}>
            Of {nf(repeat.totalBuyers)} buyers, <b>{pc(repeat.gmvFromRepeatPct)}</b> of GMV comes from repeat (2+ order) customers. A low repeat rate = leaky bucket; a high one = the problem is upstream (activation), not stickiness.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Cohort</th><th style={th}>Customers</th><th style={th}>% of buyers</th><th style={th}>GMV</th><th style={th}>% of GMV</th></tr></thead>
            <tbody>
              {[['1 order', rb.oneOrder], ['2–3 orders', rb.twoToThree], ['4+ orders', rb.fourPlus]].map(([label, b]) => b && (
                <tr key={label}><td style={td}>{label}</td><td style={td}>{nf(b.users)}</td><td style={td}>{pc(b.pct)}</td><td style={td}>{money(b.gmv)}</td><td style={{ ...td, fontWeight: 600 }}>{pc(b.gmvPct)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 3. PRICE */}
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>3 · Price truth — are we actually cheaper than Zomato/Swiggy?</h3>
          {!price?.available ? (
            <p style={{ color: '#B45309' }}>{price?.message || 'No win/loss snapshot available yet.'}</p>
          ) : (
            <>
              <p style={{ color: '#777', fontSize: 12, marginTop: 0 }}>From win/loss snapshot {price.date} · {price.summary?.restaurantsCompared || 0} restaurants compared</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {['zomato', 'swiggy'].map((pl) => {
                  const p = price.platforms[pl] || {};
                  const sum = price.summary?.[pl] || {};
                  return (
                    <div key={pl} style={{ ...card, marginBottom: 0, background: '#fafafa' }}>
                      <div style={{ fontWeight: 700, textTransform: 'capitalize', marginBottom: 8 }}>vs {pl}</div>
                      {p.carts ? <div style={{ fontSize: 14 }}>Carts: cheaper <b style={{ color: p.carts.cheaperPct >= 50 ? '#059669' : '#EF4444' }}>{pc(p.carts.cheaperPct)}</b> of {nf(p.carts.scored)} (avg {money(p.carts.avgDelta)}/cart)</div> : <div style={{ fontSize: 13, color: '#999' }}>No cart data</div>}
                      {p.realOrders ? <div style={{ fontSize: 14, marginTop: 4 }}>Real orders: cheaper <b style={{ color: p.realOrders.cheaperPct >= 50 ? '#059669' : '#EF4444' }}>{pc(p.realOrders.cheaperPct)}</b> of {nf(p.realOrders.orders)} (avg {money(p.realOrders.avgDelta)})</div> : <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>No order data</div>}
                      <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>Losing to {pl} on {nf(sum.losing)}/{nf(sum.compared)} restaurants</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* 4. SELECTION */}
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>4 · Selection — demand vs supply by cuisine (last {selection?.windowDays || 90}d)</h3>
          <p style={{ color: '#555', marginTop: 0 }}>{nf(selection?.totalOrders)} orders across {nf(selection?.liveRestaurants)} live restaurants. 🔴 = high demand per restaurant (under-supplied — add restaurants here).</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Cuisine</th><th style={th}>Orders</th><th style={th}>Order share</th><th style={th}>Live restaurants</th><th style={th}>Orders / restaurant</th><th style={th}>GMV</th></tr></thead>
            <tbody>
              {(selection?.cuisines || []).slice(0, 15).map((c) => (
                <tr key={c.cuisine} style={c.underSupplied ? { background: 'rgba(239,68,68,0.05)' } : null}>
                  <td style={td}>{c.underSupplied ? '🔴 ' : ''}{c.cuisine}</td>
                  <td style={td}>{nf(c.orders)}</td>
                  <td style={td}>{pc(c.orderSharePct)}</td>
                  <td style={td}>{nf(c.liveRestaurants)}</td>
                  <td style={{ ...td, fontWeight: c.underSupplied ? 700 : 400, color: c.underSupplied ? '#EF4444' : '#333' }}>{c.ordersPerLiveRestaurant == null ? '—' : c.ordersPerLiveRestaurant}</td>
                  <td style={td}>{money(c.gmv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default GrowthDiagnostics;
